import { AskRequest, AskRequestType } from '@hone-automation/common';
import { format } from 'date-fns';
import { mapAskRequestTypeToLabel } from 'presentation/components/KSQ2/Common/RequestType/RequestType';

// Re-export for consumers that were importing from here
export { mapAskRequestTypeToLabel };

// Type alias to avoid interface extension conflicts with AskRequest.priority
export type EnhancedAskRequest = AskRequest & {
  priority?: 'Blocker' | 'Normal';
};

export interface TransactionDetailsData {
  // Request type information
  requestType: string;
  isAskRequest: boolean;
  isFlaggedTransaction: boolean;
  isFlaggedCell: boolean;
  isCustomRequest: boolean;

  // Date information
  createdDate: string;
  trxDate: string;

  // User information
  createdByUser: string;
  updatedByUser: string;
  updatedDate: string;

  // Transaction details
  transactionName: string;
  transactionMemo: string;
  transactionAccount: string;
  transactionType: string;
  transactionAmount: string | number;
  flaggedAmount: string | number;

  // Status
  hasBlockerPriority: boolean;
}

/**
 * Hook to extract and format transaction details from a request
 * Can be used by both desktop and mobile components
 */
export function useTransactionDetails(request: EnhancedAskRequest | undefined | null): TransactionDetailsData | null {
  if (!request) {
    return null;
  }

  // Determine request type
  const requestType = mapAskRequestTypeToLabel[request.type || 'Customer Request'];
  const isAskRequest = requestType.includes('Ask') || requestType.includes('Needs Information');
  const isFlaggedTransaction = requestType.includes('Flagged transaction');
  const isFlaggedCell = requestType.includes('Flagged Cell');
  const isCustomRequest = !isAskRequest && !isFlaggedTransaction && !isFlaggedCell;

  // Format dates
  const createdDate = request.createdAt ? format(new Date(request.createdAt), 'MM/dd/yyyy') : '-';

  // Extract transaction date
  const isoString: string | undefined = (request.transaction?.date as unknown as string) || undefined;
  const trxDate = isoString
    ? (() => {
        const [year, month, day] = isoString.split('T')[0].split('-');
        return `${month}/${day}/${year}`;
      })()
    : '-';

  // Extract user information from comments
  const firstInteractionComment = request?.comments && request?.comments[0];
  // Get user name from first comment if available
  const createdByUser =
    firstInteractionComment?.user?.name || firstInteractionComment?.userLocation?.email?.split('@')[0] || 'Unknown';

  // Find the latest status from comments
  const latestStatus = request.comments
    ?.slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

  const updatedDate = latestStatus?.createdAt ? format(new Date(latestStatus.createdAt), 'MM/dd/yyyy') : '-';
  const updatedByUser =
    latestStatus?.user?.name ||
    latestStatus?.userLocation?.email?.split('@')[0] ||
    latestStatus?.userLocation?.role ||
    'Unknown';

  // Extract transaction details
  const transactionName = request.transaction?.name || '-';
  const transactionMemo = request.transaction?.memo || '-';
  const transactionAccount = request.transaction?.account || '--';
  const transactionAmount = request.transaction?.amount || '-';
  const flaggedAmount = request.flaggedAmount || transactionAmount;
  const transactionType =
    `${request.transaction?.externalTransactionType} - ${request.transaction?.externalNum}` || '-';
  // Priority status
  const hasBlockerPriority = request.priority === 'Blocker';

  return {
    requestType,
    isAskRequest,
    isFlaggedTransaction,
    isFlaggedCell,
    isCustomRequest,
    createdDate,
    trxDate,
    createdByUser,
    updatedByUser,
    updatedDate,
    transactionName,
    transactionMemo,
    transactionAccount,
    transactionType,
    transactionAmount,
    flaggedAmount,
    hasBlockerPriority,
  };
}
